import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Fab,
  SwipeableDrawer,
  IconButton,
  Divider,
  Tabs,
  Tab,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress as MuiLinearProgress,
  Tooltip,
} from '@mui/material';
import { Users, BarChart3, Plus, Filter, X, ChevronRight, Calendar, LayoutGrid, List } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { dashboardService, performanceService, taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { UserRole, TeamMemberWorkload } from '@/types';
import TeamMemberTrackingCard from '../components/TeamMemberTrackingCard';
import LeaveApprovalsView from '../components/LeaveApprovalsView';

/**
 * Maps backend TeamMemberWorkload to the member shape expected by TeamMemberTrackingCard.
 * Uses defaults for fields not provided by the workload API (department, performanceRating, skills).
 */
function workloadToMember(w: TeamMemberWorkload): {
  id: string;
  name: string;
  email: string;
  role: typeof w.role;
  department: string;
  avatar: string;
  isActive: boolean;
  performanceRating: number;
  utilizationPercent: number;
  skills: string[];
  timezone: string;
} {
  const utilization = w.utilizationPercent ?? w.utilizationPercentage ?? 0;
  return {
    id: w.id ?? w.userId,
    name: w.name ?? '',
    email: w.email ?? '',
    role: w.role,
    department: '',
    avatar: w.avatar ?? '',
    isActive: true,
    performanceRating: 0,
    utilizationPercent: utilization,
    skills: [],
    timezone: '',
  };
}

/**
 * TeamPage
 * Manager view for team oversight and bandwidth management
 * Role: Manager, TeamLead, Admin
 */
const TeamPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);

  // Sync tab from URL params (e.g. /team?tab=leaves)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'leaves') setActiveTab(1);
  }, [searchParams]);
  
  // Mobile state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  // View mode state
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() => {
    // Load from localStorage or default to 'card'
    const saved = localStorage.getItem('teamViewMode');
    return (saved === 'card' || saved === 'table') ? saved : 'card';
  });
  
  // Save view mode to localStorage
  useEffect(() => {
    localStorage.setItem('teamViewMode', viewMode);
  }, [viewMode]);
  
  // Filter states
  const [performanceFilter, setPerformanceFilter] = useState<string>('all');
  const [utilizationFilter, setUtilizationFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  
  // Date filter states
  const [dateFilterType, setDateFilterType] = useState<string>('all'); // 'all' | 'today' | 'week' | 'month' | 'range'
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Fetch team members based on current user's role
  // Admin/Super Admin: sees all non-admin users, Manager: sees only direct reports
  const { data: teamDataResponse, isLoading: teamLoading } = useQuery({
    queryKey: ['teamMembersWorkload', user?.id],
    queryFn: () => dashboardService.getTeamMembersWithWorkload(),
    enabled: !!user && [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.TEAM_LEAD].includes(user.role),
  });

  const teamData: TeamMemberWorkload[] = Array.isArray(teamDataResponse) ? teamDataResponse : [];

  /** Members list for directory: backend workload data mapped to card shape. */
  const teamMembersFromBackend = useMemo(
    () => teamData.map(workloadToMember),
    [teamData]
  );

  // Fetch tasks for all team members to derive project counts
  const { data: allMembersTasks } = useQuery({
    queryKey: ['allMembersTasks', teamData.map(t => t.id ?? t.userId).join(',')],
    queryFn: async () => {
      if (teamData.length === 0) return {};
      
      console.log('Fetching tasks for team members:', teamData.map(t => ({ id: t.id, userId: t.userId, name: t.name })));
      
      const tasksPromises = teamData.map(async (member) => {
        try {
          const memberId = member.id ?? member.userId;
          console.log(`Fetching tasks for ${member.name} (${memberId})`);
          const response = await taskService.getUserTasks(memberId, 1, 500);
          console.log(`Tasks for ${member.name}:`, response.items.length, 'tasks');
          return { userId: memberId, tasks: response.items };
        } catch (error) {
          console.error(`Error fetching tasks for ${member.name}:`, error);
          return { userId: member.id ?? member.userId, tasks: [] };
        }
      });
      
      const results = await Promise.all(tasksPromises);
      const tasksMap: Record<string, any[]> = {};
      results.forEach(result => {
        tasksMap[result.userId] = result.tasks;
        console.log(`TasksMap[${result.userId}]:`, result.tasks.length, 'tasks');
      });
      return tasksMap;
    },
    enabled: teamData.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Calculate project counts from tasks
  const projectCounts = useMemo(() => {
    if (!allMembersTasks) return {};
    
    const counts: Record<string, number> = {};
    const projectDetails: Record<string, any[]> = {};
    
    Object.entries(allMembersTasks).forEach(([userId, tasks]) => {
      const projectIds = new Set(
        tasks
          .filter((task: any) => task.projectId)
          .map((task: any) => task.projectId)
      );
      
      // Collect project details for logging
      const projects = Array.from(projectIds).map(projectId => {
        const taskWithProject = tasks.find((task: any) => task.projectId === projectId);
        return {
          id: projectId,
          name: taskWithProject?.projectName || 'Unknown',
          taskCount: tasks.filter((t: any) => t.projectId === projectId).length
        };
      });
      
      projectDetails[userId] = projects;
      counts[userId] = projectIds.size;
    });
    
    console.log('Project counts calculated:', counts);
    console.log('Project details by user:', projectDetails);
    
    return counts;
  }, [allMembersTasks]);

  // Calculate due tasks counts from tasks
  const dueTasksCounts = useMemo(() => {
    if (!allMembersTasks) return {};
    
    const counts: Record<string, number> = {};
    const today = dayjs().startOf('day');
    
    Object.entries(allMembersTasks).forEach(([userId, tasks]) => {
      const dueTasks = tasks.filter((task: any) => {
        if (!task.dueDate) return false;
        const dueDate = dayjs(task.dueDate).startOf('day');
        // Due today or overdue
        return !dueDate.isAfter(today, 'day');
      });
      counts[userId] = dueTasks.length;
    });
    return counts;
  }, [allMembersTasks]);

  // Calculate team stats
  const getTeamStats = () => {
    if (!teamData || teamData.length === 0) {
      return {
        totalMembers: 0,
        avgUtilization: 0,
        overloaded: 0,
        available: 0,
      };
    }

    const totalMembers = teamData.length;
    const avgUtilization = totalMembers > 0
      ? Math.round(
          teamData.reduce((sum: number, m: TeamMemberWorkload) => sum + (m.utilizationPercent || m.utilizationPercentage || 0), 0) /
            totalMembers
        )
      : 0;
    const overloaded = teamData.filter((m: TeamMemberWorkload) => (m.utilizationPercent || m.utilizationPercentage || 0) > 100).length;
    const available = teamData.filter((m: TeamMemberWorkload) => (m.utilizationPercent || m.utilizationPercentage || 0) < 80).length;

    return { totalMembers, avgUtilization, overloaded, available };
  };

  const stats = getTeamStats();

  // Filter and sort team members (from backend workload data)
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = teamMembersFromBackend.filter((member) => {
      // Performance filter
      if (performanceFilter !== 'all') {
        if (performanceFilter === 'excellent' && member.performanceRating < 4.5) return false;
        if (performanceFilter === 'good' && (member.performanceRating < 4.0 || member.performanceRating >= 4.5)) return false;
        if (performanceFilter === 'average' && (member.performanceRating < 3.5 || member.performanceRating >= 4.0)) return false;
        if (performanceFilter === 'poor' && member.performanceRating >= 3.5) return false;
      }

      // Utilization filter
      if (utilizationFilter !== 'all') {
        if (utilizationFilter === 'overloaded' && member.utilizationPercent <= 100) return false;
        if (utilizationFilter === 'optimal' && (member.utilizationPercent < 80 || member.utilizationPercent > 100)) return false;
        if (utilizationFilter === 'available' && member.utilizationPercent >= 80) return false;
      }

      // Department filter
      if (departmentFilter !== 'all' && member.department !== departmentFilter) return false;

      // Role filter
      if (roleFilter !== 'all' && member.role !== roleFilter) return false;

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'active' && !member.isActive) return false;
        if (statusFilter === 'inactive' && member.isActive) return false;
      }

      // Date filter (using createdAt from BaseEntity as joinDate proxy)
      if (dateFilterType !== 'all') {
        // Get the member's full data from teamData to access createdAt
        const fullMemberData = teamData.find((m: TeamMemberWorkload) => m.id === member.id || m.userId === member.id);
        // Try to get a date from various possible fields
        const memberDateStr = (fullMemberData as any)?.createdAt || (fullMemberData as any)?.joinDate || (member as any)?.createdAt;
        
        if (memberDateStr) {
          const memberDate = dayjs(memberDateStr);
          const today = dayjs();
          
          if (dateFilterType === 'today') {
            if (!memberDate.isSame(today, 'day')) return false;
          } else if (dateFilterType === 'week') {
            const startOfWeek = today.startOf('week');
            const endOfWeek = today.endOf('week');
            if (!memberDate.isBetween(startOfWeek, endOfWeek, 'day', '[]')) return false;
          } else if (dateFilterType === 'month') {
            if (!memberDate.isSame(today, 'month')) return false;
          } else if (dateFilterType === 'range') {
            if (dateFrom && !memberDate.isAfter(dayjs(dateFrom).subtract(1, 'day'))) return false;
            if (dateTo && !memberDate.isBefore(dayjs(dateTo).add(1, 'day'))) return false;
          }
        } else if (dateFilterType !== 'all') {
          // If no date available and a date filter is active, exclude this member
          return false;
        }
      }

      return true;
    });

    // Sort members
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'performance':
          return b.performanceRating - a.performanceRating;
        case 'utilization':
          return b.utilizationPercent - a.utilizationPercent;
        case 'department':
          return a.department.localeCompare(b.department);
        case 'role':
          return a.role.localeCompare(b.role);
        default:
          return 0;
      }
    });

    return filtered;
  }, [teamMembersFromBackend, performanceFilter, utilizationFilter, departmentFilter, roleFilter, statusFilter, sortBy, dateFilterType, dateFrom, dateTo, teamData]);

  // Count active filters
  const activeFiltersCount = [performanceFilter, utilizationFilter, departmentFilter, roleFilter, statusFilter].filter(f => f !== 'all').length + (dateFilterType !== 'all' ? 1 : 0);

  // Reset all filters
  const resetFilters = () => {
    setPerformanceFilter('all');
    setUtilizationFilter('all');
    setDepartmentFilter('all');
    setRoleFilter('all');
    setStatusFilter('all');
    setSortBy('name');
    setDateFilterType('all');
    setDateFrom('');
    setDateTo('');
  };

  if (teamLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width={200} height={40} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mt: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={100} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={400} sx={{ mt: 3 }} />
      </Box>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue === 0 ? 'directory' : 'leaves' });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: isMobile ? 12 : 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
            Team Management
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              {activeTab === 0 ? 'Monitor team workload, bandwidth, and performance' : 'Review and approve leave requests'}
            </Typography>
          )}
        </Box>
        {/* Desktop Action Button */}
        {!isMobile && activeTab === 0 && (
          <Button variant="contained" startIcon={<Plus size={16} />}>
            Add Team Member
          </Button>
        )}
        {/* Mobile Filter Toggle */}
        {isMobile && (
          <IconButton
            onClick={() => setMobileFilterOpen(true)}
            sx={{
              border: '1px solid',
              borderColor: activeFiltersCount > 0 ? 'primary.main' : 'divider',
              borderRadius: 2,
              color: activeFiltersCount > 0 ? 'primary.main' : 'text.secondary',
            }}
          >
            <Filter size={18} />
            {activeFiltersCount > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {activeFiltersCount}
              </Box>
            )}
          </IconButton>
        )}
      </Box>
      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            px: 2,
          }}
        >
          <Tab 
            icon={<Users size={18} />} 
            iconPosition="start" 
            label="Team Directory" 
          />
          <Tab 
            icon={<Calendar size={18} />} 
            iconPosition="start" 
            label="Leave Approvals" 
          />
        </Tabs>
      </Card>

      {/* Tab Content - Team Directory */}
      {activeTab === 0 && (
        <>      {/* Desktop Filters Section */}
      {!isMobile && (
        <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Filter size={20} />
              <Typography variant="subtitle1" fontWeight={600}>
                Filters & Sort
              </Typography>
              {activeFiltersCount > 0 && (
                <Chip 
                  label={`${activeFiltersCount} active`} 
                  size="small" 
                  color="primary" 
                  sx={{ height: 20 }}
                />
              )}
            </Box>
            {activeFiltersCount > 0 && (
              <Button 
                size="small" 
                startIcon={<X size={16} />}
                onClick={resetFilters}
              >
                Clear All
              </Button>
            )}
          </Box>
          
          <Grid container spacing={2}>
            {/* Sort By */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">Name (A-Z)</MenuItem>
                  <MenuItem value="performance">Performance (High-Low)</MenuItem>
                  <MenuItem value="utilization">Utilization (High-Low)</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                  <MenuItem value="role">Role</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Performance Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Performance</InputLabel>
                <Select
                  value={performanceFilter}
                  label="Performance"
                  onChange={(e) => setPerformanceFilter(e.target.value)}
                >
                  <MenuItem value="all">All Performance</MenuItem>
                  <MenuItem value="excellent">Excellent (4.5+)</MenuItem>
                  <MenuItem value="good">Good (4.0-4.5)</MenuItem>
                  <MenuItem value="average">Average (3.5-4.0)</MenuItem>
                  <MenuItem value="poor">Below Average (&lt;3.5)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Utilization Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Utilization</InputLabel>
                <Select
                  value={utilizationFilter}
                  label="Utilization"
                  onChange={(e) => setUtilizationFilter(e.target.value)}
                >
                  <MenuItem value="all">All Utilization</MenuItem>
                  <MenuItem value="overloaded">Overloaded (&gt;100%)</MenuItem>
                  <MenuItem value="optimal">Optimal (80-100%)</MenuItem>
                  <MenuItem value="available">Available (&lt;80%)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Department Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  <MenuItem value="Engineering">Engineering</MenuItem>
                  <MenuItem value="Design">Design</MenuItem>
                  <MenuItem value="Product">Product</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Sales">Sales</MenuItem>
                  <MenuItem value="Operations">Operations</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Role Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value={UserRole.DEPARTMENT_HEAD}>Department Head</MenuItem>
                  <MenuItem value={UserRole.PROJECT_MANAGER}>Project Manager</MenuItem>
                  <MenuItem value={UserRole.TEAM_LEAD}>Team Lead</MenuItem>
                  <MenuItem value={UserRole.MANAGER}>Manager</MenuItem>
                  <MenuItem value={UserRole.INDIVIDUAL_CONTRIBUTOR}>Contributor</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Filter</InputLabel>
                <Select
                  value={dateFilterType}
                  label="Date Filter"
                  onChange={(e) => setDateFilterType(e.target.value)}
                >
                  <MenuItem value="all">All Dates</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="range">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date Range Pickers - Shown when Custom Range selected */}
            {dateFilterType === 'range' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From Date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To Date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* Active Filters Display */}
          {activeFiltersCount > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {performanceFilter !== 'all' && (
                <Chip 
                  label={`Performance: ${performanceFilter}`} 
                  size="small" 
                  onDelete={() => setPerformanceFilter('all')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {utilizationFilter !== 'all' && (
                <Chip 
                  label={`Utilization: ${utilizationFilter}`} 
                  size="small" 
                  onDelete={() => setUtilizationFilter('all')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {departmentFilter !== 'all' && (
                <Chip 
                  label={`Department: ${departmentFilter}`} 
                  size="small" 
                  onDelete={() => setDepartmentFilter('all')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {roleFilter !== 'all' && (
                <Chip 
                  label={`Role: ${roleFilter}`} 
                  size="small" 
                  onDelete={() => setRoleFilter('all')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {dateFilterType !== 'all' && (
                <Chip 
                  label={
                    dateFilterType === 'range' 
                      ? `Date: ${dateFrom || '...'} to ${dateTo || '...'}` 
                      : `Date: ${dateFilterType}`
                  } 
                  size="small" 
                  onDelete={() => {
                    setDateFilterType('all');
                    setDateFrom('');
                    setDateTo('');
                  }}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </CardContent>
      </Card>      )}

      {/* Mobile Active Filters Display */}
      {isMobile && activeFiltersCount > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {performanceFilter !== 'all' && (
            <Chip 
              label={performanceFilter}
              size="small" 
              onDelete={() => setPerformanceFilter('all')}
              color="primary"
            />
          )}
          {utilizationFilter !== 'all' && (
            <Chip 
              label={utilizationFilter}
              size="small" 
              onDelete={() => setUtilizationFilter('all')}
              color="primary"
            />
          )}
          {departmentFilter !== 'all' && (
            <Chip 
              label={departmentFilter}
              size="small" 
              onDelete={() => setDepartmentFilter('all')}
              color="primary"
            />
          )}
          {roleFilter !== 'all' && (
            <Chip 
              label={roleFilter}
              size="small" 
              onDelete={() => setRoleFilter('all')}
              color="primary"
            />
          )}
        </Stack>
      )}
      {/* Stats Cards */}
      <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
        {/* Total Members */}
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Users size={isMobile ? 14 : 18} style={{ color: '#2563eb' }} />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  {isMobile ? 'Members' : 'Team Members'}
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
                {stats.totalMembers}
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary">
                  Active team members
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Average Utilization */}
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <BarChart3 size={isMobile ? 14 : 18} style={{ color: '#16a34a' }} />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  {isMobile ? 'Avg Util.' : 'Avg. Utilization'}
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
                {stats.avgUtilization}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stats.avgUtilization}
                sx={{
                  mt: 1,
                  height: isMobile ? 4 : 6,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  '& .MuiLinearProgress-bar': {
                    bgcolor:
                      stats.avgUtilization > 100
                        ? 'error.main'
                        : stats.avgUtilization > 80
                        ? 'warning.main'
                        : 'success.main',
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Overloaded */}
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderColor: stats.overloaded > 0 ? 'error.main' : 'divider' }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box
                  sx={{
                    width: isMobile ? 6 : 8,
                    height: isMobile ? 6 : 8,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                  }}
                />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  Overloaded
                </Typography>
              </Box>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                fontWeight={600}
                color={stats.overloaded > 0 ? 'error.main' : 'text.primary'}
              >
                {stats.overloaded}
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary">
                  Members over capacity
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Available */}
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box
                  sx={{
                    width: isMobile ? 6 : 8,
                    height: isMobile ? 6 : 8,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                  }}
                />
                <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary">
                  Available
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600} color="success.main">
                {stats.available}
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary">
                  Members with capacity
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Team Directory - No Tabs Needed */}
      <Card>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box>
            <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 700, mb: isMobile ? 0 : 1 }}>
                  {isMobile ? 'Directory' : 'Team Member Directory'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredAndSortedMembers.length} of {teamMembersFromBackend.length} members
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* View Mode Toggle */}
                {!isMobile && (
                  <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
                    <Tooltip title="Card View">
                      <IconButton
                        onClick={() => setViewMode('card')}
                        size="small"
                        sx={{
                          p: 1,
                          bgcolor: viewMode === 'card' ? 'background.paper' : 'transparent',
                          color: viewMode === 'card' ? 'primary.main' : 'text.secondary',
                          boxShadow: viewMode === 'card' ? 1 : 0,
                        }}
                      >
                        <LayoutGrid size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Table View">
                      <IconButton
                        onClick={() => setViewMode('table')}
                        size="small"
                        sx={{
                          p: 1,
                          bgcolor: viewMode === 'table' ? 'background.paper' : 'transparent',
                          color: viewMode === 'table' ? 'primary.main' : 'text.secondary',
                          boxShadow: viewMode === 'table' ? 1 : 0,
                        }}
                      >
                        <List size={18} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {filteredAndSortedMembers.length === 0 && activeFiltersCount > 0 && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<X size={16} />}
                    onClick={resetFilters}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>
            {filteredAndSortedMembers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" sx={{ mb: 1 }}>
                  No team members found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your filters
                </Typography>
              </Box>
            ) : viewMode === 'card' ? (
              // Card View
              <Grid container spacing={isMobile ? 1.5 : 3}>
                {filteredAndSortedMembers.map((member) => {
                  const workload = teamData.find(
                    (t) => t.id === member.id || t.userId === member.id
                  );
                  const tasksCount = workload?.activeTasks ?? 0;
                  // Get actual project count from tasks
                  const projectsCount = projectCounts[member.id] ?? 0;
                  const completedToday = 0;
                  // Get actual due tasks count from tasks
                  const dueTasksCount = dueTasksCounts[member.id] ?? 0;
                  
                  console.log(`Member: ${member.name}, ID: ${member.id}, Projects: ${projectsCount}`);

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
                      <TeamMemberTrackingCard
                        member={member}
                        tasksCount={tasksCount}
                        projectsCount={projectsCount}
                        completedTasksToday={completedToday}
                        dueTasksCount={dueTasksCount}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              // Table View
              <TableContainer>
                <Table size={isMobile ? 'small' : 'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Member</TableCell>
                      {!isMobile && <TableCell>Email</TableCell>}
                      <TableCell>Role</TableCell>
                      {!isMobile && <TableCell>Department</TableCell>}
                      <TableCell>Utilization</TableCell>
                      {!isMobile && <TableCell>Performance</TableCell>}
                      <TableCell align="center">Tasks</TableCell>
                      {!isMobile && <TableCell align="center">Projects</TableCell>}
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAndSortedMembers.map((member) => {
                      const workload = teamData.find(
                        (t) => t.id === member.id || t.userId === member.id
                      );
                      const tasksCount = workload?.activeTasks ?? 0;
                      // Get actual project count from tasks
                      const projectsCount = projectCounts[member.id] ?? 0;

                      const getRoleColor = (role: UserRole) => {
                        switch (role) {
                          case UserRole.SUPER_ADMIN:
                          case UserRole.ADMIN:
                            return 'error';
                          case UserRole.DEPARTMENT_HEAD:
                          case UserRole.MANAGER:
                            return 'primary';
                          case UserRole.PROJECT_MANAGER:
                            return 'info';
                          case UserRole.TEAM_LEAD:
                            return 'warning';
                          default:
                            return 'secondary';
                        }
                      };

                      const getRoleLabel = (role: UserRole): string => {
                        const labels: Record<UserRole, string> = {
                          [UserRole.SUPER_ADMIN]: 'Super Admin',
                          [UserRole.ADMIN]: 'Admin',
                          [UserRole.DEPARTMENT_HEAD]: 'Dept Head',
                          [UserRole.PROJECT_MANAGER]: 'PM',
                          [UserRole.TEAM_LEAD]: 'Team Lead',
                          [UserRole.MANAGER]: 'Manager',
                          [UserRole.INDIVIDUAL_CONTRIBUTOR]: 'Contributor',
                          [UserRole.ENGINEER]: 'Engineer',
                        };
                        return labels[role] || 'Member';
                      };

                      const getUtilizationColor = (percent: number) => {
                        if (percent > 100) return 'error';
                        if (percent > 85) return 'warning';
                        return 'success';
                      };

                      const getPerformanceLabel = (rating: number): string => {
                        if (rating >= 4.5) return 'Excellent';
                        if (rating >= 4) return 'Very Good';
                        if (rating >= 3) return 'Good';
                        if (rating >= 2) return 'Fair';
                        return 'Needs Improvement';
                      };

                      return (
                        <TableRow
                          key={member.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={() => navigate(`/team/${member.id}`)}
                        >
                          {/* Member */}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                src={member.avatar}
                                alt={member.name}
                                sx={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }}
                              >
                                {member.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {member.name}
                                </Typography>
                                {isMobile && (
                                  <Typography variant="caption" color="text.secondary">
                                    {member.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Email */}
                          {!isMobile && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {member.email}
                              </Typography>
                            </TableCell>
                          )}

                          {/* Role */}
                          <TableCell>
                            <Chip
                              label={getRoleLabel(member.role)}
                              color={getRoleColor(member.role)}
                              size="small"
                              sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
                            />
                          </TableCell>

                          {/* Department */}
                          {!isMobile && (
                            <TableCell>
                              <Typography variant="body2">
                                {member.department || 'N/A'}
                              </Typography>
                            </TableCell>
                          )}

                          {/* Utilization */}
                          <TableCell>
                            <Box sx={{ minWidth: isMobile ? 60 : 100 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color={
                                    member.utilizationPercent > 100
                                      ? 'error.main'
                                      : member.utilizationPercent > 85
                                      ? 'warning.main'
                                      : 'success.main'
                                  }
                                >
                                  {member.utilizationPercent}%
                                </Typography>
                              </Box>
                              {!isMobile && (
                                <MuiLinearProgress
                                  variant="determinate"
                                  value={Math.min(member.utilizationPercent, 100)}
                                  color={getUtilizationColor(member.utilizationPercent)}
                                  sx={{ height: 4, borderRadius: 1 }}
                                />
                              )}
                            </Box>
                          </TableCell>

                          {/* Performance */}
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {member.performanceRating.toFixed(1)}
                                </Typography>
                                <Chip
                                  label={getPerformanceLabel(member.performanceRating)}
                                  size="small"
                                  color={
                                    member.performanceRating >= 4.5
                                      ? 'success'
                                      : member.performanceRating >= 3.5
                                      ? 'warning'
                                      : 'error'
                                  }
                                  sx={{ fontSize: '0.65rem' }}
                                />
                              </Box>
                            </TableCell>
                          )}

                          {/* Tasks */}
                          <TableCell align="center">
                            <Chip
                              label={tasksCount}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>

                          {/* Projects */}
                          {!isMobile && (
                            <TableCell align="center">
                              <Chip
                                label={projectsCount}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </TableCell>
                          )}

                          {/* Actions */}
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<ChevronRight size={16} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/team/${member.id}`);
                              }}
                            >
                              {isMobile ? 'View' : 'View Profile'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Card>

      {/* Mobile Filter Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        onOpen={() => setMobileFilterOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '85vh',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Filters & Sort</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeFiltersCount > 0 && (
                <Button size="small" onClick={resetFilters}>Clear</Button>
              )}
              <IconButton onClick={() => setMobileFilterOpen(false)}>
                <X size={20} />
              </IconButton>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {/* Sort By */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Sort By</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="name">Name (A-Z)</MenuItem>
              <MenuItem value="performance">Performance</MenuItem>
              <MenuItem value="utilization">Utilization</MenuItem>
              <MenuItem value="department">Department</MenuItem>
              <MenuItem value="role">Role</MenuItem>
            </Select>
          </FormControl>

          {/* Performance */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Performance</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            {[
              { value: 'all', label: 'All' },
              { value: 'excellent', label: '4.5+' },
              { value: 'good', label: '4.0-4.5' },
              { value: 'average', label: '3.5-4.0' },
              { value: 'poor', label: '<3.5' },
            ].map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => setPerformanceFilter(opt.value)}
                variant={performanceFilter === opt.value ? 'filled' : 'outlined'}
                color={performanceFilter === opt.value ? 'primary' : 'default'}
              />
            ))}
          </Stack>

          {/* Utilization */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Utilization</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            {[
              { value: 'all', label: 'All' },
              { value: 'overloaded', label: '>100%' },
              { value: 'optimal', label: '80-100%' },
              { value: 'available', label: '<80%' },
            ].map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => setUtilizationFilter(opt.value)}
                variant={utilizationFilter === opt.value ? 'filled' : 'outlined'}
                color={utilizationFilter === opt.value ? 'primary' : 'default'}
              />
            ))}
          </Stack>

          {/* Department */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Department</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
              <MenuItem value="all">All Departments</MenuItem>
              <MenuItem value="Engineering">Engineering</MenuItem>
              <MenuItem value="Design">Design</MenuItem>
              <MenuItem value="Product">Product</MenuItem>
              <MenuItem value="Marketing">Marketing</MenuItem>
              <MenuItem value="Sales">Sales</MenuItem>
              <MenuItem value="Operations">Operations</MenuItem>
            </Select>
          </FormControl>

          {/* Role */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Role</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value={UserRole.DEPARTMENT_HEAD}>Department Head</MenuItem>
              <MenuItem value={UserRole.PROJECT_MANAGER}>Project Manager</MenuItem>
              <MenuItem value={UserRole.TEAM_LEAD}>Team Lead</MenuItem>
              <MenuItem value={UserRole.MANAGER}>Manager</MenuItem>
              <MenuItem value={UserRole.INDIVIDUAL_CONTRIBUTOR}>Contributor</MenuItem>
            </Select>
          </FormControl>

          {/* Date Filter */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Date Filter</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            {[
              { value: 'all', label: 'All' },
              { value: 'today', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'range', label: 'Range' },
            ].map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => setDateFilterType(opt.value)}
                variant={dateFilterType === opt.value ? 'filled' : 'outlined'}
                color={dateFilterType === opt.value ? 'primary' : 'default'}
              />
            ))}
          </Stack>

          {/* Date Range Pickers - Shown when Custom Range selected */}
          {dateFilterType === 'range' && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}

          {/* Apply Button */}
          <Button 
            fullWidth 
            variant="contained" 
            onClick={() => setMobileFilterOpen(false)}
            sx={{ mt: 1 }}
          >
            Apply Filters
          </Button>
        </Box>
      </SwipeableDrawer>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add team member"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Plus size={24} />
        </Fab>
      )}
        </>
      )}

      {/* Tab Content - Leave Approvals */}
      {activeTab === 1 && (
        <LeaveApprovalsView />
      )}
    </Box>
  );
};

export default TeamPage;
